import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { contractAdjustmentsService, contractsService } from '../services/api';
import { secaoCaiDeProposito, campo, useEstadoDaCarga, mensagemDoErroDeCarga } from '../design';
import { fmtDate, fmtDateTime, getDaysUntil, getTodayStr } from '../utils/formatters';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import CascaDeModal from './shared/CascaDeModal';
import { SEM_BARRA, TONE_BADGE, TONE_BARRA, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

import { Icon } from './shared/Icon';
import { ItemDeMenu } from './shared/MaisAcoes';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista,
  CelulaCliente, CelulaDinheiro, CelulaAcoes,
} from './shared/lista/PecasDaLista';
import { isAdjustmentDue } from '../utils/adjustmentVerification';
import { classificarContratos } from '../utils/situacaoDoReajuste';
import PreviaDoReajuste from './PreviaDoReajuste';
import AditivosPendentesPanel from './AditivosPendentesPanel';
import RegistrarContato, { AlvoDoContato, MarcadorDoContato } from './reajustes/RegistrarContato';
import FilaDoEspelho from './reajustes/FilaDoEspelho';
import { UltimoContato } from '../utils/ultimoContato';
import { useConfirmarPerigo } from './shared/ConfirmDialog';
import {
  ADJUSTMENT_GROUP_META,
  ADJUSTMENT_INDEX_OPTIONS,
  ADJUSTMENT_OPEN_GROUPS,
  ADJUSTMENT_STATUS_META,
  ADJUSTMENT_STATUS_ORDER,
  AdjustmentGroupKey,
  AdjustmentStatus,
  adjustmentGroupKey,
  adjustmentStatusMeta,
  baseKindMeta,
  fmtBRL,
  fmtMonthShort,
  fmtPct,
  hasDefinitiveMismatch,
  isPendingAdjustment,
} from '../utils/adjustments';

/**
 * Tela Reajustes: as anualidades de todos os contratos, agrupadas por estado e
 * ordenadas por DINHEIRO EM RISCO (diferença mensal × competências desde a
 * primeira atingida), não por data. Quem decide o que atacar primeiro é o valor,
 * não o calendário.
 *
 * Nada aqui calcula índice ou retroativo: `moneyAtRisk`, `monthsAffected` e
 * `monthlyDiff` vêm do backend (contract-adjustments.service). O front só exibe.
 */

interface Adjustment {
  id: number;
  contractId: number;
  sequence: number;
  anniversaryDate: string;
  interregnumStart?: string | null;
  interregnumEnd?: string | null;
  indexName?: string | null;
  indexFrom?: number | null;
  indexTo?: number | null;
  percentage?: number | null;
  isProvisional: boolean;
  amountBefore?: number | null;
  amountAfter?: number | null;
  effectsFrom?: string | null;
  status: AdjustmentStatus;
  oficioId?: number | null;
  apostilaAttachmentId?: string | null;
  applyAt?: string | null;
  apostilaReference?: string | null;
  complementaryInvoiceId?: number | null;
  retroactiveWaivedBefore?: string | null;
  commitmentDate?: string | null;
  commitmentSource?: string | null;
  note?: string | null;
  monthlyDiff?: number | null;
  monthsAffected?: number;
  moneyAtRisk?: number;
  contract?: any;
  oficio?: { id: number; numero?: string; status?: string; signedAt?: string | null } | null;
}

/**
 * Cor (12/10/2026, PR-2 da medição do bloco F, K1 a K4). O cartão marca pendência por BARRA e ÍCONE no tom (danger no
 * que é dinheiro parado, warning no que é trabalho a fazer); o número é neutro, e o zero não ganha verde: "0 devido" em
 * verde era a cor do NADA, e foi ela que fez a tela sem leitura parecer em ordem. O escolhido marca por peso e borda.
 */
type TomDoCartao = 'danger' | 'warning' | null;
const CARTAO = 'rounded-xl p-4 border border-l-[3px] text-left transition-colors';
const escolhido = (sim: boolean) => (sim ? 'bg-central-elevado border-slate-400' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500');
const barra = (tom: TomDoCartao) => (tom ? TONE_BARRA[tom] : SEM_BARRA);
const NumeroDoCartao: React.FC<{ tom: TomDoCartao; children: React.ReactNode; className?: string }> = ({ tom, children, className = '' }) => (
  <p className={`text-2xl font-bold mt-1 text-white flex items-center gap-1.5 ${className}`}>
    {children}
    {tom && <Icon name='alerta' className={`w-4 h-4 shrink-0 ${TONE_TEXT[tom]}`} />}
  </p>
);

interface Props {
  user: any;
  canEdit: boolean;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  onOpenContracts?: () => void;
  onEditContract?: (contract: any) => void;
  onOpenOficios?: () => void;
}

/** As colunas que ordenam (fase 4 das listas). O dinheiro começa do maior; o resto, do menor. */
type ColunaDeOrdem = 'cliente' | 'anualidade' | 'mensal' | 'efeitos' | 'risco';
const DIRECAO_INICIAL: Record<ColunaDeOrdem, 'asc' | 'desc'> = { cliente: 'asc', anualidade: 'asc', mensal: 'desc', efeitos: 'asc', risco: 'desc' };
const compararPor = (coluna: ColunaDeOrdem, x: Adjustment, y: Adjustment): number => {
  switch (coluna) {
    case 'cliente': return clientName(x.contract).localeCompare(clientName(y.contract), 'pt-BR');
    case 'anualidade': return String(x.anniversaryDate).localeCompare(String(y.anniversaryDate));
    case 'mensal': return (Number(x.amountAfter) || 0) - (Number(y.amountAfter) || 0);
    case 'efeitos': return String(x.effectsFrom || '').localeCompare(String(y.effectsFrom || ''));
    case 'risco': return (Number(x.moneyAtRisk) || 0) - (Number(y.moneyAtRisk) || 0);
  }
};

const clientName = (c: any) => c?.client?.name || c?.client?.tradeName || c?.client?.companyName || `Cliente #${c?.clientId ?? '?'}`;

const toInput = (d: any) => (d ? String(d).slice(0, 10) : '');

/**
 * Contagem regressiva do grupo "Aplica sozinho na data".
 *
 * Esse grupo não é fila de trabalho, é relógio: o cron das 06:50 aplica sem
 * ninguém clicar. Só que a data crua obriga quem lê a fazer a conta para saber
 * se é amanhã ou daqui a um mês, e é essa conta que decide se vale conferir
 * agora ou depois.
 *
 * ⚠️ Não é enfeite do cabeçalho: o dia em que o valor mensal de um contrato
 * sobe sozinho é o dia em que alguém precisa conferir no dia seguinte se subiu
 * mesmo. Cron que para em silêncio já nos custou três meses de índice parado
 * (ICTI, 28/09/2026) porque nada na tela contava o tempo.
 *
 * Mostra a aplicação MAIS PRÓXIMA da lista: é a primeira que pode falhar.
 * Só neste grupo — nos outros a data ou não existe ou já é a própria pendência.
 */
const contagemRegressiva = (
  chave: string,
  lista: readonly any[],
): { rotulo: string; ajuda: string } | null => {
  if (chave !== 'aplica_automatico') return null;

  const datas = lista.map((a: any) => toInput(a.applyAt)).filter(Boolean).sort();
  if (!datas.length) return null;

  const proxima = datas[0];
  const dias = getDaysUntil(proxima);
  if (dias == null) return null;

  const quando = dias <= 0 ? 'hoje' : dias === 1 ? 'amanhã' : `em ${dias} dias`;
  const prefixo = datas.length > 1 ? 'a próxima ' : '';

  return {
    rotulo: `${prefixo}aplica ${quando}`,
    ajuda:
      `${fmtDate(proxima)}, às 06:50, sem ninguém clicar.` +
      (datas.length > 1 ? ` Outras ${datas.length - 1} depois dessa.` : '') +
      ' No dia seguinte vale conferir se o valor novo entrou mesmo no contrato.',
  };
};

/**
 * Canais por onde um reajuste de fato anda. E-mail e WhatsApp em primeiro
 * porque são os mais usados, não porque são exceção: em 21/09/2026 três
 * decisões que mudaram o estado de três contratos chegaram por eles.
 */
const CANAIS = [
  { value: 'email', label: 'e-mail', badge: 'bg-slate-700 text-slate-300' },
  { value: 'whatsapp', label: 'WhatsApp', badge: 'bg-slate-700 text-slate-300' },
  { value: 'telefone', label: 'telefone', badge: 'bg-slate-700 text-slate-300' },
  { value: 'oficio', label: 'ofício', badge: 'bg-slate-700 text-slate-300' },
  { value: 'processo', label: 'processo', badge: 'bg-slate-700 text-slate-300' },
  { value: 'reuniao', label: 'reunião', badge: 'bg-slate-700 text-slate-300' },
  { value: 'sistema', label: 'sistema', badge: 'bg-slate-700 text-slate-300' },
  { value: 'outro', label: 'outro', badge: 'bg-slate-700 text-slate-300' },
];

const ReajustesTab: React.FC<Props> = ({ user, canEdit, showToast, onOpenContracts, onEditContract, onOpenOficios }) => {
  const confirmarPerigo = useConfirmarPerigo();
  const isAdmin = user?.role === 'admin';
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);
  const [indexValues, setIndexValues] = useState<any[]>([]);
  const [resumo, setResumo] = useState<any>(null);
  /**
   * R1 a R3 (12/10/2026, medição do bloco F; padrão liberado pelo Maestro). A carga é ESTADO. A primeira leitura que
   * falhava era só toast, e os cartões desenhavam das listas vazias NA COR DE TUDO BEM: "Devido 0" e "Em risco R$ 0,00"
   * em verde. Sem leitura, os números são "—" e a faixa diz; a recarga que falha (depois de uma ação) mantém os dados e
   * diz de quando são; o "Carregando…" da lista só na primeira carga.
   */
  const carga = useEstadoDaCarga();
  const lido = carga.atualizadoEm !== null;
  const primeiraCarga = !lido && carga.estado === 'carregando';
  // R3: as seções acessórias caem de propósito (secaoCaiDeProposito), mas agora a tela DIZ quais caíram.
  const [indisponiveis, setIndisponiveis] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [indexFilter, setIndexFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'pendentes' | AdjustmentStatus>('pendentes');
  const [showClosed, setShowClosed] = useState(false);

  const [editing, setEditing] = useState<Adjustment | null>(null);
  const [form, setForm] = useState<any>({});
  // Linha do tempo: o histórico que a `note` não guarda, porque a nota é o
  // estado atual e cada atualização apaga a anterior.
  const [timeline, setTimeline] = useState<Adjustment | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  // R4: a linha do tempo que não carregou NÃO é "nada registrado ainda" (o operador registrava de novo o que já estava lá).
  const [eventsErro, setEventsErro] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [novoEvento, setNovoEvento] = useState<any>({});
  // Dossiê: a visão por CONTRATO. A lista responde "o que está pendente"; isto
  // responde "por que este órgão está assim", que é a pergunta de quem abre um
  // contrato pela primeira vez ou volta nele seis meses depois.
  const [dossie, setDossie] = useState<any | null>(null);
  const [dossieLoading, setDossieLoading] = useState(false);
  // Rascunho do próximo e-mail. Editável dentro do dossiê; ao marcar como
  // enviado vira evento datado e some daqui.
  const [rascunho, setRascunho] = useState<any>({});
  const [rascunhoAberto, setRascunhoAberto] = useState(false);
  // A fila por VERBO. Vem do backend porque sai do mesmo alarmBuckets que monta
  // o e-mail das 07h: tela e alarme não podem discordar sobre o que fazer.
  const [fila, setFila] = useState<any | null>(null);
  /** Q1 (31/10/2026): a caixinha "Registrei contato" aberta na própria linha. `chave` diz qual linha. */
  const [contatoDe, setContatoDe] = useState<(AlvoDoContato & { chave: string }) | null>(null);

  const load = useCallback(() => carga.rodar(async () => {
      const [adj, con, idx, res, fil] = await Promise.all([
        contractAdjustmentsService.getAll(),
        contractsService.getAll(),
        contractAdjustmentsService.getIndexValues().catch(secaoCaiDeProposito('só exibe o último índice publicado; a lista de anualidades segue valendo')),
        // O resumo é acessório: se ele falhar, a lista de anualidades continua
        // valendo. Cair a tela inteira por causa do cabeçalho seria pior.
        contractAdjustmentsService.getResumo().catch(secaoCaiDeProposito('o resumo é cabeçalho acessório; a lista de anualidades segue valendo')),
        // A fila idem: se falhar, a tela cai no agrupamento por estado, que é
        // o comportamento antigo. Pior que feio é ficar sem lista.
        contractAdjustmentsService.getFila().catch(secaoCaiDeProposito('sem a fila, a tela agrupa por estado, que é o comportamento antigo')),
      ]);
      setAdjustments(Array.isArray(adj) ? adj : adj?.data || []);
      setContracts(Array.isArray(con) ? con : con?.data || []);
      setIndexValues(Array.isArray(idx) ? idx : []);
      setResumo(res && Array.isArray(res.buckets) ? res : null);
      setFila(fil && Array.isArray(fil.grupos) ? fil : null);
      setIndisponiveis([
        ...(res === null ? ['a situação por cliente'] : []),
        ...(fil === null ? ['a fila por verbo (a lista agrupa por estado, e pode discordar do alarme das 07h)'] : []),
        ...(idx === null ? ['o último índice publicado'] : []),
      ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  useEffect(() => { load(); }, [load]);

  // ── Cards ──────────────────────────────────────────────────────────────
  const activeContracts = useMemo(() => contracts.filter((c: any) => c.status === 'Ativo'), [contracts]);
  /**
   * Quem entra na classificação por situação. Ativo sempre; Suspenso só chega ao
   * balde `sem_dado_do_orgao`, e o próprio `for` abaixo garante isso ao manter a
   * ordem das condições — um suspenso que caísse em `vencido_sem_anualidade`
   * inventaria perda num contrato que ainda não fatura.
   */
  const contratosClassificaveis = useMemo(
    () => contracts.filter((c: any) => c.status === 'Ativo' || c.status === 'Suspenso'),
    [contracts],
  );
  const devidoContracts = useMemo(
    () => activeContracts.filter((c: any) => isAdjustmentDue(c, getDaysUntil(c.nextAdjustmentDate))),
    [activeContracts],
  );
  const unverifiedContracts = useMemo(
    () => activeContracts.filter((c: any) => (c.adjustmentVerificationStatus || 'nao_conferido') === 'nao_conferido'),
    [activeContracts],
  );
  const pendingAdjustments = useMemo(() => adjustments.filter((a) => isPendingAdjustment(a.status)), [adjustments]);

  /**
   * O último contato por CONTRATO (backend #579), das três fontes que o trazem: as anualidades, e os contratos de
   * "Em dia" e "Falta dado do órgão" do resumo. ⚠️ Contrato que não está em nenhuma delas fica FORA do mapa, e a tela não
   * escreve nada sobre ele: ausente é "não sei", e só o `null` que veio do servidor quer dizer "ninguém registrou".
   */
  const ultimoContatoPorContrato = useMemo(() => {
    const m = new Map<number, UltimoContato | null>();
    for (const a of adjustments as any[]) if ('ultimoContato' in a) m.set(Number(a.contractId), a.ultimoContato ?? null);
    for (const lista of [resumo?.emDia, resumo?.semDadoDoOrgao]) {
      for (const c of Array.isArray(lista) ? lista : []) if ('ultimoContato' in c) m.set(Number(c.contratoId), c.ultimoContato ?? null);
    }
    return m;
  }, [adjustments, resumo]);
  const contatoRegistrado = () => { setContatoDe(null); showToast('Contato registrado', 'success'); load(); };

  /**
   * PAINEL DOS CARDS — qual lista está aberta embaixo dos números.
   *
   * O número sozinho não resolve: "Reajuste devido 9" e o grupo "Devida 3
   * anualidades" usam a mesma palavra para denominadores diferentes (9 CONTRATOS
   * com aniversário vencido, 3 ANUALIDADES no estado devida), e sem poder abrir
   * os 9 não há como saber quem são. Clicar no card abre a lista.
   */
  const [painel, setPainel] = useState<string | null>(null);

  /**
   * Quem está em cada balde. A regra mora em `utils/situacaoDoReajuste`, com
   * teste: a ordem das condições É a regra, e cair no balde errado não dá erro
   * em lugar nenhum. Os CONTADORES seguem vindo do backend; se divergirem, o
   * backend é que vale.
   */
  const contratosPorSituacao = useMemo(
    () => classificarContratos(contratosClassificaveis, {
      comAnualidadeViva: new Set(
        adjustments.filter((a) => isPendingAdjustment(a.status)).map((a: any) => Number(a.contractId)),
      ),
      comAlgumaAnualidade: new Set(adjustments.map((a: any) => Number(a.contractId))),
      hoje: new Date().toISOString().slice(0, 10),
    }),
    [contratosClassificaveis, adjustments],
  );

  const totalRisk = useMemo(() => pendingAdjustments.reduce((s, a) => s + (Number(a.moneyAtRisk) || 0), 0), [pendingAdjustments]);
  const provisionals = useMemo(() => pendingAdjustments.filter((a) => a.isProvisional), [pendingAdjustments]);
  // Quanto a recorrência sobe quando os órgãos deferirem. Difere do "em risco": aquele é
  // o que já deixou de entrar; este é o mensal daqui em diante. Anualidades encadeadas
  // (1ª e 2ª do mesmo contrato) somam certo, porque a 2ª parte do valor da 1ª.
  const mrrUplift = useMemo(() => {
    const soma = (list: Adjustment[]) =>
      list.reduce((s, a) => s + (Number(a.amountAfter) || 0) - (Number(a.amountBefore) || 0), 0);
    const total = soma(pendingAdjustments);
    const provisorio = soma(pendingAdjustments.filter((a) => a.isProvisional));
    return { total, provisorio, definitivo: total - provisorio };
  }, [pendingAdjustments]);
  const mismatches = useMemo(() => adjustments.filter(hasDefinitiveMismatch), [adjustments]);

  const latestIndex = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of indexValues) {
      if (!m[r.indexName] || r.referenceMonth > m[r.indexName]) m[r.indexName] = r.referenceMonth;
    }
    return m;
  }, [indexValues]);

  // ── Filtro + agrupamento ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return adjustments.filter((a) => {
      if (indexFilter && String(a.indexName || '').toUpperCase() !== indexFilter) return false;
      if (statusFilter === 'pendentes' && !isPendingAdjustment(a.status)) return false;
      // O card "Provisórias" não abre painel de contrato: ele filtra a própria
      // lista, porque o que interessa ali é a ANUALIDADE (qual índice está
      // provisório), não o contrato.
      if (painel === 'provisorias' && !a.isProvisional) return false;
      if (statusFilter && statusFilter !== 'pendentes' && a.status !== statusFilter) return false;
      if (q) {
        const hay = [
          clientName(a.contract),
          a.contract?.client?.companyName,
          a.contract?.contractNumber,
          a.contract?.code,
          `#${a.contractId}`,
          a.note,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [adjustments, search, indexFilter, statusFilter, painel]);

  const groups = useMemo(() => {
    const byStatus = new Map<AdjustmentGroupKey, Adjustment[]>();
    for (const s of ADJUSTMENT_STATUS_ORDER) byStatus.set(s, []);
    byStatus.set('a_vencer', []);
    // A seção sai do estado MAIS a data: `devida`/`ressalvada` com aniversário
    // no futuro vão para "A vencer". Ver adjustmentGroupKey.
    for (const a of filtered) byStatus.get(adjustmentGroupKey(a as any))?.push(a);
    byStatus.forEach((list) => {
      list.sort((x, y) => {
        const risk = (Number(y.moneyAtRisk) || 0) - (Number(x.moneyAtRisk) || 0);
        if (risk !== 0) return risk;
        return String(x.anniversaryDate) < String(y.anniversaryDate) ? -1 : 1;
      });
    });
    return byStatus;
  }, [filtered]);

  // ── Ações ──────────────────────────────────────────────────────────────
  const refreshIndexes = async () => {
    setBusy('indices');
    try {
      const r = await contractAdjustmentsService.refreshIndexValues();
      const falhas = (r?.fontes || []).filter((f: any) => f.error);
      const fechadas = r?.provisorias?.settled ?? 0;
      const mudou = r?.provisorias?.changed?.length ?? 0;
      showToast(
        `Índices atualizados${falhas.length ? ` (falha: ${falhas.map((f: any) => f.indexName).join(', ')})` : ''}. ` +
          `Provisórias fechadas: ${fechadas}${mudou ? `, com índice diferente: ${mudou}` : ''}.`,
        falhas.length ? 'error' : 'success',
      );
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Erro ao atualizar índices', 'error');
    } finally {
      setBusy(null);
    }
  };

  const generateAll = async () => {
    setBusy('gerar');
    try {
      const r = await contractAdjustmentsService.generateAll();
      showToast(
        `${r?.created ?? 0} anualidade(s) nova(s) em ${r?.contracts ?? 0} contratos conferidos` +
          (r?.errors?.length ? `; ${r.errors.length} erro(s)` : ''),
        r?.errors?.length ? 'error' : 'success',
      );
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Erro ao gerar anualidades', 'error');
    } finally {
      setBusy(null);
    }
  };

  const openEdit = (a: Adjustment) => {
    setEditing(a);
    setForm({
      status: a.status,
      indexName: a.indexName || '',
      // toFixed(4) evita o ruído de ponto flutuante (0.052635 × 100 = 5.2635000000000005).
      percentagePct: a.percentage === null || a.percentage === undefined ? '' : (Number(a.percentage) * 100).toFixed(4).replace(/0+$/, '').replace(/\.$/, ''),
      isProvisional: !!a.isProvisional,
      amountBefore: a.amountBefore ?? '',
      amountAfter: a.amountAfter ?? '',
      anniversaryDate: toInput(a.anniversaryDate),
      effectsFrom: toInput(a.effectsFrom),
      retroactiveWaivedBefore: toInput(a.retroactiveWaivedBefore),
      commitmentDate: toInput(a.commitmentDate),
      commitmentSource: a.commitmentSource || '',
      note: a.note || '',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy('salvar');
    try {
      const pct = form.percentagePct === '' ? null : Number(String(form.percentagePct).replace(',', '.')) / 100;
      await contractAdjustmentsService.update(editing.id, {
        status: form.status,
        indexName: form.indexName || null,
        percentage: pct,
        isProvisional: !!form.isProvisional,
        amountBefore: form.amountBefore === '' ? null : Number(form.amountBefore),
        amountAfter: form.amountAfter === '' ? null : Number(form.amountAfter),
        anniversaryDate: form.anniversaryDate,
        effectsFrom: form.effectsFrom || null,
        retroactiveWaivedBefore: form.retroactiveWaivedBefore || null,
        commitmentDate: form.commitmentDate || null,
        commitmentSource: form.commitmentSource || null,
        note: form.note || null,
      });
      showToast('Anualidade salva', 'success');
      setEditing(null);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      showToast(Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao salvar', 'error');
    } finally {
      setBusy(null);
    }
  };

  const gerarOficio = async (a: Adjustment) => {
    setBusy(`oficio-${a.id}`);
    try {
      const o = await contractAdjustmentsService.createOficio(a.id);
      showToast(`${o?.numero || 'Ofício'} criado como rascunho para ${clientName(a.contract)}. Revise e emita em Administrativo → Ofícios.`, 'success');
      await load();
      onOpenOficios?.();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      showToast(Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao gerar o ofício', 'error');
    } finally {
      setBusy(null);
    }
  };

  // ── Registrar apostila/aditivo ────────────────────────────────────────
  const [apostila, setApostila] = useState<{ adj: Adjustment; file: File | null; amountAfter: string; effectsFrom: string; reference: string; applyAt: string; generateInvoice: boolean; dueDate: string; preview: any | null } | null>(null);

  /**
   * Primeiro dia seguro para aplicar o valor novo no contrato, ou '' quando já
   * passou (aplicação imediata). A fatura emitida no dia 1 do mês M cobre o
   * serviço de M-1, então aplicar antes da geração do mês dos efeitos faria a
   * fatura daquele mês sair reajustada sem que o mês reajustado tenha começado.
   */
  const diaSeguroParaAplicar = (effectsFrom: string): string => {
    if (!effectsFrom) return '';
    const [y, m] = effectsFrom.slice(0, 10).split('-');
    const seguro = `${y}-${m}-02`;
    return seguro > getTodayStr() ? seguro : '';
  };

  const openApostila = (a: Adjustment) => {
    const effectsFrom = toInput(a.effectsFrom);
    setApostila({
      adj: a,
      file: null,
      amountAfter: a.amountAfter != null ? String(a.amountAfter) : '',
      effectsFrom,
      reference: a.apostilaReference || '',
      applyAt: toInput(a.applyAt) || diaSeguroParaAplicar(effectsFrom),
      generateInvoice: true,
      dueDate: '',
      preview: null,
    });
  };

  const apostilaPayload = (dryRun: boolean, attachmentId?: string | null) => ({
    dryRun,
    attachmentId: attachmentId || null,
    amountAfter: apostila!.amountAfter === '' ? null : Number(String(apostila!.amountAfter).replace(',', '.')),
    effectsFrom: apostila!.effectsFrom || null,
    reference: apostila!.reference || null,
    applyAt: apostila!.applyAt || null,
    generateInvoice: apostila!.generateInvoice,
    dueDate: apostila!.dueDate || null,
  });

  const previewApostila = async () => {
    if (!apostila) return;
    setBusy('apostila-preview');
    try {
      const p = await contractAdjustmentsService.registerApostila(apostila.adj.id, apostilaPayload(true));
      setApostila({ ...apostila, preview: p });
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      showToast(Array.isArray(msg) ? msg.join('; ') : msg || 'Erro na prévia', 'error');
    } finally {
      setBusy(null);
    }
  };

  const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const confirmApostila = async () => {
    if (!apostila) return;
    setBusy('apostila');
    try {
      let attachmentId: string | null = null;
      if (apostila.file) {
        // Anexo da apostila no contrato, categoria aditivo: é o documento que prova o deferimento.
        const contract = await contractsService.addAttachment(apostila.adj.contractId, {
          filename: apostila.file.name,
          fileBase64: await fileToBase64(apostila.file),
          description: `Apostila/aditivo do reajuste (${apostila.adj.sequence}ª anualidade)${apostila.reference ? ` — ${apostila.reference}` : ''}`,
          category: 'aditivo',
        } as any);
        const anexos: any[] = contract?.attachments || [];
        attachmentId = anexos.length ? anexos[anexos.length - 1].id : null;
      }
      const r = await contractAdjustmentsService.registerApostila(apostila.adj.id, apostilaPayload(false, attachmentId));
      showToast(
        r?.scheduled
          ? `Apostila agendada para ${fmtDate(r.applyAt)}: o contrato segue em ${fmtBRL(r?.contract?.mrrBefore)} até lá.`
          : r?.invoice?.code
            ? `Apostila registrada: contrato em ${fmtBRL(r.contract?.mrrAfter)} e complementar ${r.invoice.code} (${fmtBRL(r.invoice.totalValue)}) criada como rascunho.`
            : `Apostila registrada: contrato em ${fmtBRL(r?.contract?.mrrAfter)}. ${r?.statusAfter === 'deferida' ? 'Complementar não gerada; gere depois.' : 'Sem retroativo a cobrar.'}`,
        'success',
      );
      setApostila(null);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      showToast(Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao registrar a apostila', 'error');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (a: Adjustment) => {
    if (!await confirmarPerigo(`Excluir a ${a.sequence}ª anualidade de ${clientName(a.contract)}? Isso não desfaz ofício nem fatura.`)) return;
    setBusy('excluir');
    try {
      await contractAdjustmentsService.delete(a.id);
      showToast('Anualidade excluída', 'success');
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Erro ao excluir', 'error');
    } finally {
      setBusy(null);
    }
  };

  const abrirTimeline = async (a: Adjustment) => {
    setTimeline(a);
    setNovoEvento({ occurredOn: getTodayStr(), channel: 'email', direction: 'recebido', summary: '', counterpart: '' });
    setEventsLoading(true);
    setEventsErro(null);
    try {
      setEvents(await contractAdjustmentsService.listEvents(a.id));
    } catch (e) {
      setEvents([]);
      setEventsErro(mensagemDoErroDeCarga(e));
    } finally {
      setEventsLoading(false);
    }
  };

  const abrirDossie = async (contractId: number) => {
    setDossieLoading(true);
    setDossie({ carregando: true });
    setRascunhoAberto(false);
    try {
      const d = await contractAdjustmentsService.getDossie(contractId);
      setDossie(d);
      setRascunho(d?.rascunho || {});
    } catch (e) {
      setDossie(null);
      showToast(`Não consegui carregar o dossiê deste contrato (${mensagemDoErroDeCarga(e)})`, 'error');
    } finally {
      setDossieLoading(false);
    }
  };

  const salvarRascunho = async () => {
    if (!dossie?.contrato?.id) return;
    setBusy('rascunho');
    try {
      const salvo = await contractAdjustmentsService.salvarRascunho(dossie.contrato.id, rascunho);
      setRascunho(salvo || {});
      setDossie({ ...dossie, rascunho: salvo });
      showToast(salvo ? 'Rascunho guardado' : 'Rascunho apagado', 'success');
    } catch {
      showToast('Não consegui guardar o rascunho', 'error');
    } finally {
      setBusy(null);
    }
  };

  const marcarRascunhoEnviado = async () => {
    if (!dossie?.contrato?.id) return;
    setBusy('rascunho');
    try {
      await contractAdjustmentsService.rascunhoEnviado(dossie.contrato.id, { occurredOn: getTodayStr() });
      // Recarrega: o rascunho sai e a linha do tempo ganha o evento. É o ponto
      // do desenho — o lembrete vira histórico datado, não um check que some.
      await abrirDossie(dossie.contrato.id);
      showToast('Registrado como enviado na linha do tempo', 'success');
    } catch {
      showToast('Não consegui registrar o envio', 'error');
    } finally {
      setBusy(null);
    }
  };

  const salvarEvento = async () => {
    if (!timeline) return;
    setBusy('evento');
    try {
      await contractAdjustmentsService.addEvent(timeline.id, {
        occurredOn: novoEvento.occurredOn,
        channel: novoEvento.channel,
        direction: novoEvento.direction,
        summary: novoEvento.summary,
        counterpart: novoEvento.counterpart || null,
      });
      setNovoEvento({ ...novoEvento, summary: '', counterpart: '' });
      setEvents(await contractAdjustmentsService.listEvents(timeline.id));
      showToast('Registrado', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      showToast(Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao registrar', 'error');
    } finally {
      setBusy(null);
    }
  };

  const apagarEvento = async (eventId: number) => {
    if (!timeline) return;
    try {
      await contractAdjustmentsService.removeEvent(timeline.id, eventId);
      setEvents(await contractAdjustmentsService.listEvents(timeline.id));
    } catch {
      showToast('Erro ao apagar', 'error');
    }
  };

  const today = getTodayStr();

  // ── Ordenação (fase 4 das listas, 22/10/2026) ──────────────────────────
  // Sem coluna escolhida, cada seção segue a ordem dela: por estado, o dinheiro em risco; na fila, a do backend (a mesma
  // do alarme das 07h). Escolhida, vale para todas as seções ao mesmo tempo; o segundo clique inverte e o terceiro volta
  // à ordem da seção.
  const [ordem, setOrdem] = useState<{ coluna: ColunaDeOrdem; dir: 'asc' | 'desc' } | null>(null);
  const ordenarPor = (coluna: ColunaDeOrdem) => {
    const inicial = DIRECAO_INICIAL[coluna];
    setOrdem((o) => (o?.coluna !== coluna ? { coluna, dir: inicial } : o.dir === inicial ? { coluna, dir: inicial === 'asc' ? 'desc' : 'asc' } : null));
  };
  const ordenar = (lista: Adjustment[]): Adjustment[] => {
    if (!ordem) return lista;
    const sinal = ordem.dir === 'asc' ? 1 : -1;
    return [...lista].sort((x, y) => sinal * compararPor(ordem.coluna, x, y));
  };
  const coluna = (c: ColunaDeOrdem) => ({ ordem: ordem?.coluna === c ? ordem.dir : (false as const), onOrdenar: () => ordenarPor(c) });

  // O mesmo cabeçalho em todas as seções: fixo, e a ordenação vale para todas.
  const cabecalho = (
    <CabecalhoDaLista>
      <ColunaDaLista rotulo='Cliente / contrato' {...coluna('cliente')} />
      <ColunaDaLista rotulo='Anualidade' largura='w-32' {...coluna('anualidade')} />
      <ColunaDaLista rotulo='Índice' largura='w-28' />
      <ColunaDaLista rotulo='Mensal' largura='w-32' alinhamento='direita' {...coluna('mensal')} />
      <ColunaDaLista rotulo='Efeitos desde' largura='w-40' {...coluna('efeitos')} />
      <ColunaDaLista rotulo={<span title='Diferença mensal × competências desde a primeira atingida (descontada a renúncia)'>Em risco</span>} largura='w-40' alinhamento='direita' {...coluna('risco')} />
      <ColunaDaLista rotulo='Estado' largura='w-56' />
      <ColunaDaLista rotulo={<span className='sr-only'>Ações</span>} largura='w-48' />
    </CabecalhoDaLista>
  );

  // ── Render ─────────────────────────────────────────────────────────────
  const renderRow = (a: Adjustment) => {
    const c = a.contract || {};
    const kind = baseKindMeta(c.adjustmentBaseKind);
    const meta = adjustmentStatusMeta(a.status);
    const mismatch = hasDefinitiveMismatch(a);
    const vencida = String(a.anniversaryDate).slice(0, 10) <= today;
    const risk = Number(a.moneyAtRisk) || 0;
    const pendente = isPendingAdjustment(a.status);
    // D1: o VERBO da anualidade fica à vista, no lugar do editar. Sem ofício ainda, o próximo passo é gerá-lo; com o
    // ofício (ou deferida), é registrar a apostila. O outro verbo, quando cabe, vai para o ⋯.
    const oficio = canEdit && pendente && !a.oficioId && a.percentage !== null && a.percentage !== undefined
      ? {
          rotulo: busy === `oficio-${a.id}` ? 'Gerando…' : 'Gerar ofício',
          onClick: () => gerarOficio(a),
          desabilitado: busy === `oficio-${a.id}`,
          title: 'Cria o rascunho do ofício de reajuste desta anualidade, já vinculado, com o corpo no padrão da casa (de ofício / mediante pedido / silente). Revise e emita em Administrativo → Ofícios.',
        }
      : null;
    const apostila = canEdit && pendente && a.amountBefore !== null && a.amountBefore !== undefined
      ? {
          rotulo: a.status === 'deferida' ? 'Complementar' : 'Registrar apostila',
          onClick: () => openApostila(a),
          title: 'O órgão deferiu: anexa a apostila/aditivo, reajusta os itens e o mensal do contrato, gera a fatura complementar do retroativo e fecha a anualidade.',
        }
      : null;
    const verbo = oficio || apostila || undefined;
    const outroVerbo = oficio && apostila ? apostila : null;
    const itens: ItemDeMenu[] = [
      ...(outroVerbo ? [{ label: outroVerbo.rotulo, onClick: outroVerbo.onClick, title: outroVerbo.title }] : []),
      ...(verbo && canEdit ? [{ label: 'Editar anualidade', onClick: () => openEdit(a) }] : []),
      { label: 'Linha do tempo', onClick: () => abrirTimeline(a), title: 'Linha do tempo: tudo o que aconteceu com esta anualidade, inclusive o que chegou por e-mail e WhatsApp.' },
      ...(onEditContract
        ? [{
            label: 'Abrir assinatura',
            title: 'Abrir assinatura (regra do reajuste)',
            onClick: () => {
              const full = contracts.find((x: any) => x.id === a.contractId);
              if (full) onEditContract(full);
            },
          }]
        : []),
      ...(isAdmin && a.status !== 'aplicada' && a.status !== 'deferida'
        ? [{ label: 'Excluir', onClick: () => remove(a), danger: true, separadoAntes: true }]
        : []),
    ];
    return (
      <React.Fragment key={a.id}>
      <LinhaDaLista>
        <CelulaCliente
          nome={
            // O nome abre o DOSSIÊ do contrato. A linha continua sendo da anualidade; o dossiê é o passo atrás, com a
            // regra, a cadeia inteira de valores e a conversa com o órgão.
            <button
              type='button'
              onClick={() => abrirDossie(Number(a.contractId))}
              className='font-medium text-white truncate max-w-full text-left hover:underline decoration-dotted underline-offset-2'
              title={`${c.client?.companyName || ''}\nAbrir o dossiê deste contrato.`}
            >
              {clientName(c)}
            </button>
          }
          razao={c.client?.companyName && c.client.companyName !== clientName(c) ? c.client.companyName : null}
          outras={[`Assinatura ${a.contractId}`, c.contractNumber ? `Contrato ${c.contractNumber}` : null]}
          extra={
            <div className='flex flex-wrap gap-1 mt-1'>
              {kind && <span className='text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300' title={kind.hint}>base: {kind.short}</span>}
              {c.adjustmentVerifiedAt ? (
                <span className='text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300' title={c.adjustmentRuleSource ? `Regra em: ${c.adjustmentRuleSource}` : ''}>
                  {c.adjustmentExOfficio === false ? 'mediante pedido' : 'de ofício'}
                </span>
              ) : (
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${TONE_BADGE.warning}`} title='A regra deste contrato ainda não foi lida no documento. Os números vieram de ofício já enviado.'>regra não conferida</span>
              )}
            </div>
          }
        />
        <td className={CELULA}>
          <p className='text-white text-sm whitespace-nowrap'>{a.sequence}ª · {fmtDate(a.anniversaryDate)}</p>
          <p className='text-xs text-slate-500 truncate'>
            {a.interregnumStart ? `${fmtMonthShort(a.interregnumStart)} – ${fmtMonthShort(a.interregnumEnd)}` : 'interregno a definir'}
            {!vencida && <span className='ml-1 text-slate-600'>(a vencer)</span>}
          </p>
        </td>
        <td className={CELULA}>
          <p className='text-white text-sm whitespace-nowrap'>{a.indexName || '—'} <span className='font-semibold'>{fmtPct(a.percentage)}</span></p>
          <div className='flex flex-wrap gap-1 mt-1'>
            {a.isProvisional && <span className={`text-[10px] px-1.5 py-0.5 rounded ${TONE_BADGE.warning}`} title='Índice do mês-alvo ainda não publicado: usado o último disponível. O cron fecha quando sair.'>provisório</span>}
            {mismatch && <span className={`text-[10px] px-1.5 py-0.5 rounded ${TONE_BADGE.danger}`} title={a.note || ''}>definitivo difere</span>}
            {a.indexFrom !== null && a.indexFrom !== undefined && a.indexTo !== null && a.indexTo !== undefined && (
              <span className='text-[10px] text-slate-500' title='números-índice: de → para'>{a.indexFrom} → {a.indexTo}</span>
            )}
          </div>
        </td>
        {/* E1: o mensal NOVO em branco; o anterior e a diferença embaixo. */}
        <CelulaDinheiro valor={a.amountAfter}>
          {a.amountBefore !== null && a.amountBefore !== undefined && <p className='text-xs text-slate-500'>de {fmtBRL(a.amountBefore)}</p>}
          {a.monthlyDiff !== null && a.monthlyDiff !== undefined && (
            <p className='text-xs text-slate-400'>{Number(a.monthlyDiff) > 0 ? '+' : ''}{fmtBRL(a.monthlyDiff)}/mês</p>
          )}
        </CelulaDinheiro>
        <td className={CELULA}>
          <p className='text-slate-300 text-sm'>{a.effectsFrom ? fmtDate(a.effectsFrom) : '—'}</p>
          <p className='text-xs text-slate-500 truncate'>
            {pendente ? `${a.monthsAffected ?? 0} competência(s)` : ''}
            {a.retroactiveWaivedBefore ? ` · renúncia até ${fmtDate(a.retroactiveWaivedBefore)}` : ''}
          </p>
        </td>
        <CelulaDinheiro valor={pendente ? risk : null}>
          {a.oficioId && (
            <p className='text-[10px] text-slate-500 truncate' title={a.oficio?.status ? `Ofício ${a.oficio.status}` : ''}>
              {a.oficio?.numero || `ofício ${a.oficioId}`}{a.oficio?.status ? ` · ${a.oficio.status}` : ''}
            </p>
          )}
          {a.complementaryInvoiceId && <p className='text-[10px] text-slate-500'>complementar {a.complementaryInvoiceId}</p>}
        </CelulaDinheiro>
        <td className={CELULA}>
          <div className='flex flex-wrap gap-1'>
            <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${meta.badge}`} title={meta.hint}>{meta.label}</span>
            {a.applyAt && a.status === 'deferida' && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${toInput(a.applyAt) < getTodayStr() ? TONE_BADGE.danger : TONE_BADGE.neutral}`}
                title={
                  toInput(a.applyAt) < getTodayStr()
                    ? 'O dia de aplicar já passou e a anualidade continua deferida: conferir o cron das 06:50.'
                    : 'O valor novo entra no contrato nesta data, depois do faturamento do mês.'
                }
              >
                {toInput(a.applyAt) < getTodayStr() ? 'atrasada desde ' : 'aplica em '}{fmtDate(a.applyAt)}
              </span>
            )}
            {a.commitmentDate && pendente && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${toInput(a.commitmentDate) < today ? TONE_BADGE.danger : TONE_BADGE.neutral}`}
                title={
                  (toInput(a.commitmentDate) < today
                    ? 'O órgão furou o prazo que ele mesmo marcou. É isto que se cita na próxima cobrança.'
                    : 'Dentro do prazo que o órgão deu: o alarme das 07h não cobra até lá.') +
                  (a.commitmentSource ? `\n\n${a.commitmentSource}` : '')
                }
              >
                {toInput(a.commitmentDate) < today ? 'furou ' : 'aguarda até '}{fmtDate(a.commitmentDate)}
              </span>
            )}
          </div>
          {a.note && !mismatch && <p className='text-[10px] text-slate-500 mt-1 line-clamp-2' title={a.note}>{a.note}</p>}
          {mismatch && <p className='text-[10px] text-red-400/80 mt-1 line-clamp-2' title={a.note || ''}>{a.note}</p>}
          {/* M1: o último contato com o órgão, abaixo dos selos. */}
          {'ultimoContato' in a && <MarcadorDoContato ultimoContato={(a as any).ultimoContato} />}
          {canEdit && (
            <button
              type='button'
              onClick={() => setContatoDe(contatoDe?.chave === `a-${a.id}` ? null : { chave: `a-${a.id}`, adjustmentId: a.id, contractId: Number(a.contractId), cliente: clientName(c) })}
              aria-expanded={contatoDe?.chave === `a-${a.id}`}
              className='mt-1 text-[11px] text-slate-400 hover:text-white underline decoration-dotted underline-offset-2'
              data-registrei-contato
            >
              Registrei contato
            </button>
          )}
        </td>
        <CelulaAcoes
          rotuloDoMenu={`Mais ações da anualidade ${a.id}`}
          verbo={verbo}
          onEditar={canEdit ? () => openEdit(a) : undefined}
          itens={itens}
          Icon={Icon}
        />
      </LinhaDaLista>
      {contatoDe?.chave === `a-${a.id}` && (
        <tr>
          <td colSpan={8} className='px-4 pb-3'>
            <RegistrarContato alvo={contatoDe} onRegistrado={contatoRegistrado} onClose={() => setContatoDe(null)} />
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  // Uma seção: a casca da lista (sem overflow, para o cabeçalho fixar), a faixa de título dela e a tabela.
  const secao = (chave: string, titulo: React.ReactNode, lista: Adjustment[]) => (
    <CascaDaLista key={chave}>
      {titulo}
      <TabelaDaLista>
        {cabecalho}
        <tbody className='divide-y divide-slate-800'>{ordenar(lista).map(renderRow)}</tbody>
      </TabelaDaLista>
    </CascaDaLista>
  );

  const renderGroup = (status: AdjustmentGroupKey) => {
    const list = groups.get(status) || [];
    if (!list.length) return null;
    const meta = ADJUSTMENT_GROUP_META[status];
    const risk = list.reduce((s, a) => s + (Number(a.moneyAtRisk) || 0), 0);
    return secao(status, (
      <div className='px-4 py-3 border-b border-central-borda flex items-center gap-3'>
        <span className={`text-xs px-2 py-0.5 rounded ${meta.badge}`}>{meta.label}</span>
        <span className='text-sm text-slate-400'>{list.length} anualidade{list.length > 1 ? 's' : ''}</span>
        {status !== 'a_vencer' && isPendingAdjustment(status) && risk > 0 && <span className='text-sm text-white font-semibold ml-auto'>{fmtBRL(risk)} em risco</span>}
        <span className='text-xs text-slate-500 ml-auto hidden lg:inline'>{meta.hint}</span>
      </div>
    ), list);
  };

  /**
   * A FILA POR VERBO. Um bloco por ação, na ordem de prioridade que o backend
   * decidiu — a mesma do alarme das 07h.
   *
   * Os filtros de busca e índice continuam valendo: a interseção com `filtered`
   * reaproveita a mesma regra em vez de reimplementá-la aqui.
   */
  const renderGrupoDaFila = (g: any) => {
    const permitidos = new Set(filtered.map((a) => a.id));
    const lista = (g.anualidades || []).filter((a: any) => permitidos.has(a.id));
    if (!lista.length) return null;
    const risco = lista.reduce((s: number, a: any) => s + (Number(a.moneyAtRisk) || 0), 0);
    const contagem = contagemRegressiva(g.chave, lista);
    return secao(g.chave, (
      <div className={`px-4 py-3 border-b border-central-borda flex items-center gap-3 flex-wrap ${g.acionavel ? '' : 'opacity-80'}`}>
        <span className={`text-sm font-semibold ${g.acionavel ? 'text-white' : 'text-slate-400'}`}>
          {g.acionavel && <span className='text-white mr-1.5'>▶</span>}
          {g.verbo}
        </span>
        <span className='text-xs text-slate-500 font-mono border border-slate-700 rounded-full px-2 py-0.5'>{lista.length}</span>
        {contagem && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${TONE_BADGE.neutral} whitespace-nowrap`}
            title={contagem.ajuda}
          >
            {contagem.rotulo}
          </span>
        )}
        {risco > 0 && (
          <span className={`text-sm font-medium ${g.acionavel ? 'text-white' : 'text-slate-500'}`}>{fmtBRL(risco)}</span>
        )}
        <span className='text-xs text-slate-500 ml-auto hidden lg:inline'>{g.porque}</span>
      </div>
    ), lista);
  };

  const openStatuses: AdjustmentGroupKey[] = ADJUSTMENT_OPEN_GROUPS;
  const closedStatuses: AdjustmentGroupKey[] = ['aplicada', 'nao_aplicavel', 'renunciada'];
  const closedCount = closedStatuses.reduce((n, s) => n + (groups.get(s)?.length || 0), 0);

  const faixa = `px-4 py-3 rounded-xl border text-sm flex items-center justify-between gap-3 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`;
  return (
    <div className='space-y-6'>
      {carga.estado === 'erro' && (
        <div role='alert' className={faixa} data-reajustes-faixa={lido ? 'desatualizado' : 'sem-leitura'}>
          <span>
            {lido && carga.atualizadoEm
              ? `Não consegui atualizar os reajustes (${carga.erro}). Mostrando os lidos em ${fmtDateTime(carga.atualizadoEm)}.`
              : `Não consegui ler os reajustes (${carga.erro}). Os números ficam em "—" até ler: zero aqui diria que não há reajuste devido.`}
          </span>
          <button type='button' onClick={() => void load()} className='shrink-0 underline font-medium'>Tentar de novo</button>
        </div>
      )}
      {lido && indisponiveis.length > 0 && (
        <p className={`text-xs ${TONE_TEXT.warning}`} data-reajustes-indisponivel>
          Indisponível agora: {indisponiveis.join('; ')}. O resto da tela segue valendo.
        </p>
      )}
      <div>
        <h2 className='text-2xl font-bold text-white'>Reajustes</h2>
        <p className='text-slate-400 text-sm'>
          Agrupadas pelo que precisa ser feito, na mesma ordem do alarme das 07h. O contrato guarda a regra; a anualidade guarda o fato.
          {Object.keys(latestIndex).length > 0 && (
            <span className='ml-2 text-slate-500'>
              Índices no cache: {['IPCA', 'INPC', 'IST', 'ICTI'].filter((k) => latestIndex[k]).map((k) => `${k} ${fmtMonthShort(latestIndex[k])}`).join(' · ')}
            </span>
          )}
        </p>
      </div>

      {/* L1 (31/10/2026): o cartão do espelho da tratativa, com a fila dos e-mails que o HUB só palpitou numa gaveta.
          Só admin: as rotas do arquivo de e-mail são ADMIN. */}
      {isAdmin && (
        <div className='flex flex-wrap gap-4'>
          <FilaDoEspelho contratos={contratosClassificaveis} anualidades={adjustments as any} onConfirmado={load} />
        </div>
      )}

      {/* Cards: os dois números do prompt (devido / a conferir) + risco + aumento + provisórias */}
      <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4'>
        <button onClick={() => setPainel(painel === 'devido' ? null : 'devido')} className={`${CARTAO} ${escolhido(painel === 'devido')} ${barra(lido && devidoContracts.length > 0 ? 'danger' : null)}`} aria-pressed={painel === 'devido'} title='Regra conferida no documento E anualidade vencida sem aplicar. É dinheiro na mesa. Clique para ver quais.'>
          <p className='text-sm text-slate-400'>Reajuste devido</p>
          <NumeroDoCartao tom={lido && devidoContracts.length > 0 ? 'danger' : null}>{lido ? devidoContracts.length : '—'}</NumeroDoCartao>
          <p className='text-xs text-slate-500 mt-1'>contratos conferidos e vencidos</p>
        </button>
        <button onClick={() => setPainel(painel === 'a_conferir' ? null : 'a_conferir')} className={`${CARTAO} ${escolhido(painel === 'a_conferir')} ${barra(lido && unverifiedContracts.length > 0 ? 'warning' : null)}`} aria-pressed={painel === 'a_conferir'} title='Índice e data-base nunca lidos no documento. Clique para ver quais.'>
          <p className='text-sm text-slate-400'>A conferir</p>
          <NumeroDoCartao tom={lido && unverifiedContracts.length > 0 ? 'warning' : null}>{lido ? unverifiedContracts.length : '—'}</NumeroDoCartao>
          <p className='text-xs text-slate-500 mt-1'>contratos sem a regra lida no documento</p>
        </button>
        <button onClick={() => { setPainel(null); setStatusFilter('pendentes'); }} className={`${CARTAO} ${escolhido(false)} ${barra(lido && totalRisk > 0 ? 'danger' : null)}`} title='Soma da diferença mensal × competências já faturadas a menor, das anualidades devidas, pedidas e deferidas. Clique para filtrar a lista pelas pendentes.'>
          <p className='text-sm text-slate-400'>Em risco</p>
          <NumeroDoCartao tom={lido && totalRisk > 0 ? 'danger' : null} className='whitespace-nowrap'>{lido ? fmtBRL(totalRisk) : '—'}</NumeroDoCartao>
          <p className='text-xs text-slate-500 mt-1'>{lido ? `${pendingAdjustments.length} anualidade(s) pendente(s)` : 'sem leitura'}</p>
        </button>
        <button onClick={() => { setPainel(null); setStatusFilter('pendentes'); }} className={`${CARTAO} ${escolhido(false)} ${SEM_BARRA}`} title='Soma das diferenças mensais das anualidades pendentes. É quanto a recorrência sobe quando os órgãos deferirem, não o que já foi perdido. Clique para filtrar a lista pelas pendentes.'>
          <p className='text-sm text-slate-400'>Aumento de recorrência</p>
          <p className='text-2xl font-bold mt-1 whitespace-nowrap text-white'>{lido ? <>{fmtBRL(mrrUplift.total)}<span className='text-sm font-normal text-slate-500'>/mês</span></> : '—'}</p>
          <p className='text-xs text-slate-500 mt-1'>
            {fmtBRL(mrrUplift.total * 12)} no ano
            {mrrUplift.provisorio > 0 && <> · {fmtBRL(mrrUplift.provisorio)} ainda provisório</>}
          </p>
        </button>
        <button onClick={() => setPainel(painel === 'provisorias' ? null : 'provisorias')} className={`${CARTAO} ${escolhido(painel === 'provisorias')} ${barra(!lido ? null : mismatches.length > 0 ? 'danger' : provisionals.length > 0 ? 'warning' : null)}`} aria-pressed={painel === 'provisorias'} title='Anualidades calculadas com índice ainda não definitivo. O cron dos dias 12 e 20 fecha e avisa quando o definitivo difere. Clique para ver quais.'>
          <p className='text-sm text-slate-400'>Provisórias</p>
          <NumeroDoCartao tom={!lido ? null : mismatches.length > 0 ? 'danger' : provisionals.length > 0 ? 'warning' : null}>{lido ? provisionals.length : '—'}</NumeroDoCartao>
          <p className='text-xs text-slate-500 mt-1'>{mismatches.length > 0 ? `${mismatches.length} com definitivo diferente do pedido` : 'aguardando índice definitivo'}</p>
        </button>
      </div>

      {/* ── SITUAÇÃO POR CLIENTE ──────────────────────────────────────────────
          Os cards acima contam ANUALIDADES: respondem "o que está pendente" e
          não respondem "quem está resolvido". Sem esse outro lado, todo contrato
          parece suspeito e os mesmos documentos são reabertos toda semana.

          E o antigo "A conferir" somava 111 contratos PRIVADOS com 18 PÚBLICOS:
          129 parece intransponível, 18 é uma tarde de trabalho. Reajuste de
          privado se negocia; de órgão público se prova em documento, e é esse
          que precisa de controle. Por isso os dois saem separados aqui.

          Classificação e contagem vêm do backend (helpers/situacao-reajuste.util),
          que tem guardrail. Aqui só se desenha. */}
      {resumo && (
        <div className='bg-slate-800/30 rounded-xl border border-slate-700 p-4'>
          <p className='text-sm text-slate-400 mb-3'>Situação por cliente</p>
          <div className='grid grid-cols-2 lg:grid-cols-5 gap-3'>
            {resumo.buckets.map((b: any) => {
              const meta: Record<string, { rotulo: string; ajuda: string; tom: TomDoCartao }> = {
                pendente: {
                  rotulo: 'Com anualidade viva',
                  ajuda: 'Tem anualidade devida, pedida ou deferida. É o que está em curso.',
                  tom: 'warning',
                },
                vencido_sem_anualidade: {
                  rotulo: 'Vencido sem anualidade',
                  ajuda: 'Regra conferida, aniversário já passou e ninguém gerou a anualidade. É o furo mais silencioso: não aparece em lista nenhuma.',
                  tom: 'danger',
                },
                sem_dado_do_orgao: {
                  rotulo: 'Falta dado do órgão',
                  ajuda: 'Documento lido e cláusula anotada, mas a data-base não existe em documento nenhum: ela só está no processo do órgão. Sem ela não há aniversário. O trabalho aqui é perguntar, não ler.',
                  tom: 'warning',
                },
                a_conferir: {
                  rotulo: 'Documento não lido',
                  ajuda: 'Ninguém abriu o edital ou o contrato para saber índice e data-base. Sem isso não há aniversário, e sem aniversário o cron não gera nada.',
                  tom: 'warning',
                },
                em_dia: {
                  rotulo: 'Em dia',
                  ajuda: 'Regra lida no documento, nenhuma anualidade pendente e próximo aniversário ainda no futuro.',
                  tom: null,
                },
              };
              const m = meta[b.situacao];
              if (!m) return null;
              const destaque = b.situacao === 'a_conferir' ? b.publicos : b.total;
              return (
                <button
                  key={b.situacao}
                  type='button'
                  onClick={() => setPainel(painel === b.situacao ? null : b.situacao)}
                  className={`rounded-lg p-3 border border-l-[3px] text-left transition-colors ${escolhido(painel === b.situacao)} ${barra(destaque > 0 ? m.tom : null)}`}
                  aria-pressed={painel === b.situacao}
                  title={`${m.ajuda} Clique para ver a lista.`}
                >
                  <p className='text-xs text-slate-400'>{m.rotulo}</p>
                  <NumeroDoCartao tom={destaque > 0 ? m.tom : null}>{destaque}</NumeroDoCartao>
                  <p className='text-xs text-slate-500 mt-1'>
                    {b.situacao === 'a_conferir'
                      ? <>órgãos públicos · <span className='text-slate-600'>{b.privados} privados</span></>
                      : <>{b.publicos} público(s){b.privados > 0 && <> · {b.privados} privado(s)</>}</>}
                  </p>
                  {b.situacao === 'pendente' && b.emRisco > 0 && (
                    <p className='text-xs text-red-400/80 mt-1'>{fmtBRL(b.emRisco)} em risco</p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Contador sozinho não evita esquecimento; a DATA evita. Quem está em
              dia só está até um dia certo, e é esse dia que precisa estar à vista.
              Os chips continuam sempre visíveis; o painel abre por cima deles. */}
          {resumo.emDia?.length > 0 && painel !== 'em_dia' && (
            <div className='mt-3 flex flex-wrap gap-2'>
              {/* Chip clicável: "em dia" deixou de ser beco sem saída. Quem
                  está em dia também tem história — a regra que prova, a cadeia
                  já aplicada e a conversa que levou até aqui. */}
              {resumo.emDia.map((e: any) => (
                <button
                  key={e.contratoId}
                  type='button'
                  onClick={() => abrirDossie(Number(e.contratoId))}
                  className='text-xs px-2 py-1 rounded-md bg-slate-800/60 text-slate-300 border border-slate-700 hover:border-slate-500'
                  title={`Em dia até o próximo aniversário. MRR ${fmtBRL(e.mrr)}. Clique para abrir o dossiê.`}
                >
                  {e.cliente}
                  {e.ateQuando && <span className='text-slate-500'> até {fmtDate(e.ateQuando)}</span>}
                </button>
              ))}
            </div>
          )}

          {/* FALTA DADO DO ÓRGÃO — bloco próprio, não chip. Chip serve para o
              que está resolvido; isto é trabalho, e de dois tipos que não podem
              parecer a mesma coisa: quem ainda não foi perguntado depende de
              nós, quem já foi depende do órgão. Sem data-base o contrato nunca
              entra no gerador, então esta é a única tela em que ele aparece. */}
          {/* Lista PRÓPRIA, não um balde da fila: a fila é de anualidades e
              agrupa por verbo, e estes documentos existem justamente porque a
              anualidade ainda não existe. Inventar uma pseudo-anualidade para
              caber nela quebraria as duas invariantes da fila (exaustiva e
              disjunta) e passaria a somar risco de linha que não existe. */}
          <AditivosPendentesPanel canEdit={canEdit} onAplicado={load} />

          {resumo.semDadoDoOrgao?.length > 0 && painel !== 'sem_dado_do_orgao' && (
            <div className={`mt-3 rounded-lg border p-3 ${TONE_BORDER.warning} ${TONE_SURFACE.warning}`}>
              <p className={`text-xs font-medium ${TONE_TEXT.warning}`}>
                Travados por um dado que só o órgão tem
                <span className='text-slate-400 font-normal'> · sem a data-base não nasce anualidade</span>
              </p>
              <div className='mt-2 flex flex-col gap-1.5'>
                {resumo.semDadoDoOrgao.map((c: any) => {
                  const dias = c.pedidoEm ? getDaysUntil(c.pedidoEm) : null;
                  return (
                    <button
                      key={c.contratoId}
                      type='button'
                      onClick={() => abrirDossie(Number(c.contratoId))}
                      className='w-full flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-md bg-slate-800/60 border border-slate-700 hover:border-slate-500 text-left'
                      title='Abrir o dossiê: a regra já lida, o que falta e se o órgão já foi perguntado.'
                    >
                      <span className='min-w-0'>
                        <span className='flex items-center gap-2 min-w-0'>
                          <span className='text-sm text-white truncate'>{c.cliente}</span>
                          {c.suspenso && (
                            <span className='shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-600/60 text-slate-300'
                              title='Contrato suspenso: o serviço ainda não foi ativado. Está aqui porque a data-base continua faltando, e perguntar ao órgão não depende da ativação.'>
                              suspenso
                            </span>
                          )}
                        </span>
                        <span className='block text-xs text-slate-500'>
                          #{c.contratoId}
                          {!c.isPublicSector && <> · privado</>}
                        </span>
                      </span>
                      <span className='text-right shrink-0'>
                        {/* Suspenso chega com mrr ZERO de propósito: somá-lo no
                            risco de quem não fatura inventaria perda que não
                            está acontecendo. Mas imprimir "R$ 0,00" faria a
                            linha parecer cadastro errado — o traço diz que não
                            há valor a contar, que é outra coisa. */}
                        {c.suspenso
                          ? <span className='block text-sm text-slate-500 whitespace-nowrap' title='Não entra na conta de risco: o contrato ainda não fatura.'>—</span>
                          : <span className='block text-sm text-slate-200 whitespace-nowrap'>{fmtBRL(Number(c.mrr) || 0)}</span>}
                        {c.pedidoEm ? (
                          <span className='block text-xs text-slate-500 whitespace-nowrap'>
                            perguntamos em {fmtDate(c.pedidoEm)}
                            {dias !== null && dias < 0 && <> · há {Math.abs(dias)}d</>}
                          </span>
                        ) : (
                          <span className={`block text-xs whitespace-nowrap ${TONE_TEXT.warning}`}>falta perguntar</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* PAINEL: quem está no card clicado. Abre com a lista inteira, com
              cliente, MRR, próximo aniversário e o que falta em cada um, e o
              clique na linha abre a assinatura para resolver ali mesmo. */}
          {painel && painel !== 'provisorias' && (() => {
            const rotulos: Record<string, string> = {
              devido: 'Conferidos e com aniversário vencido',
              pendente: 'Com anualidade viva',
              vencido_sem_anualidade: 'Vencidos sem anualidade gerada',
              sem_dado_do_orgao: 'Travados por um dado que só o órgão tem',
              a_conferir: 'Documento nunca lido',
              em_dia: 'Em dia',
            };
            const lista: any[] = painel === 'devido' ? devidoContracts : (contratosPorSituacao[painel] || []);
            const publicos = lista.filter((c: any) => c?.client?.isPublicSector);
            const privados = lista.filter((c: any) => !c?.client?.isPublicSector);
            // Uma porta só: em qualquer lista, o clique abre o DOSSIÊ. A
            // assinatura continua a um clique, pelo botão de dentro dele.
            // Antes cada lista levava a um lugar diferente e a leitura do
            // contrato dependia de lembrar por onde entrar.
            // A linha abre o dossiê, como antes; o "Registrei contato" é um botão à parte, com a caixinha embaixo (Q1), e
            // grava na linha do tempo do CONTRATO quando ele não tem anualidade (é o caso de "Falta dado do órgão").
            const linha = (c: any) => (
              <div key={c.id} className='space-y-1.5'>
              <div className='flex items-stretch gap-1.5'>
              <button
                type='button'
                onClick={() => abrirDossie(Number(c.id))}
                className='flex-1 min-w-0 flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 hover:border-slate-500 text-left'
                title='Abrir o dossiê deste contrato.'
              >
                <span className='min-w-0'>
                  <span className='flex items-center gap-2 min-w-0'>
                    <span className='text-sm text-white truncate'>{clientName(c)}</span>
                    {c.status === 'Suspenso' && (
                      <span className='shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-600/60 text-slate-300'
                        title='Contrato suspenso: o serviço ainda não foi ativado. Só aparece no balde do dado que falta ao órgão.'>
                        suspenso
                      </span>
                    )}
                  </span>
                  <span className='block text-xs text-slate-500'>
                    #{c.id}
                    {c.contractNumber && <> · Contrato {c.contractNumber}</>}
                    {c.adjustmentIndex && <> · {c.adjustmentIndex}</>}
                  </span>
                  {ultimoContatoPorContrato.has(Number(c.id)) && <MarcadorDoContato ultimoContato={ultimoContatoPorContrato.get(Number(c.id))} />}
                </span>
                <span className='text-right shrink-0'>
                  <span className='block text-sm text-slate-200 whitespace-nowrap'>{fmtBRL(Number(c.mrr) || 0)}</span>
                  <span className='block text-xs text-slate-500 whitespace-nowrap'>
                    {c.nextAdjustmentDate ? <>próx. {fmtDate(c.nextAdjustmentDate)}</> : 'sem data-base'}
                  </span>
                </span>
              </button>
              {canEdit && (
                <button
                  type='button'
                  onClick={() => setContatoDe(contatoDe?.chave === `c-${c.id}` ? null : { chave: `c-${c.id}`, adjustmentId: null, contractId: Number(c.id), cliente: clientName(c) })}
                  aria-expanded={contatoDe?.chave === `c-${c.id}`}
                  className='shrink-0 px-2.5 rounded-lg text-xs text-slate-300 border border-slate-700 hover:border-slate-500'
                  data-registrei-contato
                >
                  Registrei contato
                </button>
              )}
              </div>
              {contatoDe?.chave === `c-${c.id}` && (
                <RegistrarContato alvo={contatoDe} onRegistrado={contatoRegistrado} onClose={() => setContatoDe(null)} />
              )}
              </div>
            );
            return (
              <div className='mt-3 rounded-lg border border-slate-700 bg-slate-900/40 p-3'>
                <div className='flex items-center justify-between mb-2'>
                  <p className='text-sm text-slate-300'>{rotulos[painel] || painel} <span className='text-slate-500'>({lista.length})</span></p>
                  <button type='button' onClick={() => setPainel(null)} className='text-xs text-slate-400 hover:text-white'>fechar</button>
                </div>
                {lista.length === 0 && <p className='text-xs text-slate-500'>Nenhum contrato neste estado.</p>}
                {publicos.length > 0 && (
                  <>
                    <p className='text-xs text-slate-500 mb-1'>Órgão público ({publicos.length})</p>
                    <div className='grid gap-1.5 md:grid-cols-2'>{publicos.map(linha)}</div>
                  </>
                )}
                {privados.length > 0 && (
                  <>
                    <p className='text-xs text-slate-500 mt-3 mb-1'>Privado ({privados.length})</p>
                    <div className='grid gap-1.5 md:grid-cols-2'>{privados.map(linha)}</div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      <ListToolbar
        search={<ToolbarSearch value={search} onChange={setSearch} placeholder='Cliente, nº do contrato, nota...' />}
        filters={<>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className={`px-3 py-2 ${campo} rounded-lg text-sm`}>
            <option value='pendentes'>Pendentes</option>
            <option value=''>Todos os estados</option>
            {ADJUSTMENT_STATUS_ORDER.map((s) => <option key={s} value={s}>{ADJUSTMENT_STATUS_META[s].label}</option>)}
          </select>
          <select value={indexFilter} onChange={(e) => setIndexFilter(e.target.value)} className={`px-3 py-2 ${campo} rounded-lg text-sm`}>
            <option value=''>Todos os índices</option>
            {ADJUSTMENT_INDEX_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </>}
        actions={isAdmin ? (
          <div className='flex gap-2'>
            <button onClick={refreshIndexes} disabled={!!busy} className='px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-2' title='Busca IPCA, INPC, IST e ICTI nas fontes e fecha as anualidades provisórias. O cron faz isso nos dias 12 e 20.'>
              <Icon name='refresh' className='w-4 h-4' /> {busy === 'indices' ? 'Atualizando…' : 'Atualizar índices'}
            </button>
            <button onClick={generateAll} disabled={!!busy} className='px-3 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-2' title='Gera as anualidades vencidas de todo contrato com regra conferida. O cron faz isso todo dia às 06:40.'>
              <Icon name='plus' className='w-4 h-4' /> {busy === 'gerar' ? 'Gerando…' : 'Gerar vencidas'}
            </button>
          </div>
        ) : undefined}
      />

      {primeiraCarga ? (
        <p className='text-slate-500 text-center py-12'>Carregando…</p>
      ) : !lido ? (
        <p className='text-slate-400 text-center py-12' data-reajustes-sem-leitura>Sem leitura das anualidades: não dá para dizer o que está devido.</p>
      ) : filtered.length === 0 ? (
        <p className='text-slate-500 text-center py-12'>Nenhuma anualidade com estes filtros.</p>
      ) : (
        <div className='space-y-4'>
          {/* Por VERBO quando se olha o que está pendente, que é a pergunta de
              quem abre a tela. Filtrando por um estado específico, volta o
              agrupamento por estado: aí a pergunta é outra. */}
          {fila && statusFilter === 'pendentes'
            ? fila.grupos.map(renderGrupoDaFila)
            : openStatuses.map(renderGroup)}
          {statusFilter === 'pendentes' ? null : closedCount > 0 && (
            <div>
              <button onClick={() => setShowClosed((v) => !v)} className='text-sm text-slate-400 hover:text-white flex items-center gap-2'>
                <span className='text-xs'>{showClosed ? '▾' : '▸'}</span>
                {showClosed ? 'Ocultar' : 'Mostrar'} encerradas ({closedCount})
              </button>
              {showClosed && <div className='space-y-4 mt-4'>{closedStatuses.map(renderGroup)}</div>}
            </div>
          )}
        </div>
      )}

      {/* Modal: registrar apostila/aditivo (fecha o ciclo: deferida → contrato → complementar → aplicada) */}
      {apostila && (
        // Campos digitados: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
        // Só moldura: o fluxo continua Prévia (dryRun) → Confirmar, e o Confirmar
        // segue travado sem prévia. A primária azul vira o laranja.
        <CascaDeModal
          largura='md'
          titulo={`Registrar apostila · ${apostila.adj.sequence}ª anualidade`}
          subtitulo={clientName(apostila.adj.contract)}
          onClose={() => setApostila(null)}
          corpoClassName='p-6 space-y-4'
          rodape={
            <>
              <button onClick={() => setApostila(null)} className='px-4 py-2 text-slate-300 hover:text-white'>Cancelar</button>
              <div className='ml-auto flex gap-2'>
                <button onClick={previewApostila} disabled={!!busy} className='px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg disabled:opacity-50'>{busy === 'apostila-preview' ? 'Calculando…' : 'Prévia'}</button>
                <button onClick={confirmApostila} disabled={!!busy || !apostila.preview} className='px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg disabled:opacity-50' title={!apostila.preview ? 'Veja a prévia antes de confirmar' : ''}>{busy === 'apostila' ? 'Registrando…' : 'Confirmar'}</button>
              </div>
            </>
          }
        >
            <div>
              <p className='text-xs text-slate-500'>
                O órgão deferiu. Isto reajusta os itens e o mensal da assinatura pelo valor deferido, gera a fatura complementar do retroativo
                (da primeira competência atingida até a última já faturada) e fecha a anualidade. Regra da casa: o mensal só muda depois da apostila.
              </p>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='md:col-span-3'>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Apostila / termo aditivo (PDF) <span className='text-slate-500 font-normal'>— vai para os anexos da assinatura, categoria "aditivo"</span></label>
                <label
                  className='flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-central-borda rounded-lg text-sm text-slate-400 hover:border-blue-500 cursor-pointer'
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-blue-500'); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('ring-2', 'ring-blue-500')}
                  onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('ring-2', 'ring-blue-500'); const f = e.dataTransfer.files?.[0]; if (f) setApostila({ ...apostila, file: f }); }}
                >
                  <Icon name='upload' className='w-4 h-4' />
                  {apostila.file ? apostila.file.name : 'Arraste o PDF aqui ou clique para escolher (opcional, mas recomendado)'}
                  <input type='file' accept='application/pdf,image/*' className='hidden' onChange={(e) => setApostila({ ...apostila, file: e.target.files?.[0] || null })} />
                </label>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Valor mensal deferido (R$)</label>
                <input type='text' inputMode='decimal' value={apostila.amountAfter} onChange={(e) => setApostila({ ...apostila, amountAfter: e.target.value, preview: null })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>Calculado: {fmtBRL(apostila.adj.amountAfter)}. Mude só se o termo disser outro (arredondamento item a item).</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Efeitos desde</label>
                <input type='date' value={apostila.effectsFrom} onChange={(e) => setApostila({ ...apostila, effectsFrom: e.target.value, preview: null })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>1ª competência atingida, conforme o termo.</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Referência do termo</label>
                <input type='text' value={apostila.reference} onChange={(e) => setApostila({ ...apostila, reference: e.target.value, preview: null })} placeholder='Termo de Apostilamento nº 01/2026, Processo SEI…' maxLength={300} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>Entra na descrição e nas observações da complementar.</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Aplicar no contrato em</label>
                <input type='date' value={apostila.applyAt} onChange={(e) => setApostila({ ...apostila, applyAt: e.target.value, preview: null })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>Data futura agenda: o cron das 06:50 aplica no dia. Vazio aplica agora.</p>
              </div>
              {(() => {
                // A fatura do dia 1 cobre o serviço do mês anterior. Aplicar antes da
                // geração do mês dos efeitos contamina justamente a fatura que ainda
                // deve sair pelo valor antigo. O aviso só aparece quando é esse o caso.
                const seguro = diaSeguroParaAplicar(apostila.effectsFrom);
                if (!seguro || (apostila.applyAt && apostila.applyAt >= seguro)) return null;
                return (
                  <div className={`md:col-span-3 border rounded-lg p-3 text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>
                    <p className='font-medium'>A fatura de {fmtMonthShort(apostila.effectsFrom)} ainda não foi gerada.</p>
                    <p className='mt-1 text-amber-200/80'>
                      Ela cobre o serviço do mês anterior e deve sair pelo valor antigo. Aplicando agora, ela sairia já reajustada.
                      O primeiro dia seguro é {fmtDate(seguro)}, depois do faturamento.
                    </p>
                    <button onClick={() => setApostila({ ...apostila, applyAt: seguro, preview: null })} className='mt-2 px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded'>
                      Agendar para {fmtDate(seguro)}
                    </button>
                  </div>
                );
              })()}
              <div className='md:col-span-2'>
                <label className='flex items-center gap-2 text-sm text-slate-300'>
                  <input type='checkbox' checked={apostila.generateInvoice} onChange={(e) => setApostila({ ...apostila, generateInvoice: e.target.checked, preview: null })} />
                  Gerar a fatura complementar do retroativo (rascunho, sem contrato vinculado)
                </label>
                <p className='text-[10px] text-slate-500 mt-1'>Desmarque se o órgão dispensou o retroativo ou se vai cobrar de outro jeito. A anualidade fica "deferida" e o botão "Complementar" continua disponível.</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Vencimento da complementar</label>
                <input type='date' value={apostila.dueDate} onChange={(e) => setApostila({ ...apostila, dueDate: e.target.value, preview: null })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>Vazio = prazo do cadastro do cliente, senão +28 dias.</p>
              </div>
            </div>

            {/* Mesmo desenho da prévia do aditivo: os dois caminhos devolvem
                o mesmo objeto, porque passam pelo mesmo registerApostila. */}
            <PreviaDoReajuste preview={apostila.preview} />
        </CascaDeModal>
      )}

      {/* Modal de edição */}
      {editing && (
        // Campos digitados: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
        <CascaDeModal
          largura='base'
          titulo={`${editing.sequence}ª anualidade · ${clientName(editing.contract)}`}
          subtitulo={`Contrato #${editing.contractId}${editing.contract?.contractNumber ? ` · ${editing.contract.contractNumber}` : ''}`}
          onClose={() => setEditing(null)}
          topo={<p className='px-6 pt-3 text-xs text-slate-500'>Percentual em %, com até 4 casas (IST e ICTI vêm de números-índice).</p>}
          corpoClassName='p-6 space-y-4'
          rodape={
            <div className='ml-auto flex gap-2'>
              <button onClick={() => setEditing(null)} className='px-4 py-2 text-slate-300 hover:text-white'>Cancelar</button>
              <button onClick={saveEdit} disabled={busy === 'salvar'} className='px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg disabled:opacity-50'>{busy === 'salvar' ? 'Salvando…' : 'Salvar'}</button>
            </div>
          }
        >
            <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Estado</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}>
                  {ADJUSTMENT_STATUS_ORDER.map((s) => <option key={s} value={s}>{ADJUSTMENT_STATUS_META[s].label}</option>)}
                </select>
                <p className='text-[10px] text-slate-500 mt-1'>{adjustmentStatusMeta(form.status).hint}</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Aniversário</label>
                <input type='date' value={form.anniversaryDate} onChange={(e) => setForm({ ...form, anniversaryDate: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Índice</label>
                <select value={form.indexName} onChange={(e) => setForm({ ...form, indexName: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}>
                  <option value=''>—</option>
                  {ADJUSTMENT_INDEX_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Percentual (%)</label>
                <input type='text' inputMode='decimal' value={form.percentagePct} onChange={(e) => setForm({ ...form, percentagePct: e.target.value })} placeholder='4,3915' className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <label className='flex items-center gap-2 mt-2 text-xs text-slate-400'>
                  <input type='checkbox' checked={!!form.isProvisional} onChange={(e) => setForm({ ...form, isProvisional: e.target.checked })} /> índice provisório
                </label>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Mensal antes (R$)</label>
                <input type='number' step='0.01' value={form.amountBefore} onChange={(e) => setForm({ ...form, amountBefore: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Mensal depois (R$)</label>
                <input type='number' step='0.01' value={form.amountAfter} onChange={(e) => setForm({ ...form, amountAfter: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>antes × (1 + %) tem que dar isto; senão o backend recusa.</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Efeitos desde</label>
                <input type='date' value={form.effectsFrom} onChange={(e) => setForm({ ...form, effectsFrom: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>1ª competência atingida (padrão: 1º mês cheio após o aniversário).</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Renúncia às diferenças antes de</label>
                <input type='date' value={form.retroactiveWaivedBefore} onChange={(e) => setForm({ ...form, retroactiveWaivedBefore: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>Só o retroativo de exercício encerrado. Nunca o percentual.</p>
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-300 mb-1'>Órgão prometeu para</label>
                <input type='date' value={form.commitmentDate} onChange={(e) => setForm({ ...form, commitmentDate: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                <p className='text-[10px] text-slate-500 mt-1'>Silencia o alarme até esta data. Depois dela vira prazo furado.</p>
              </div>
            </div>
            {/* A data sozinha não se sustenta: daqui a um mês ninguém lembra de
                onde ela saiu, e o compromisso quase sempre chega por e-mail ou
                WhatsApp, fora do HUB. Por isso a fonte fica ao lado e vai junto
                para o e-mail do alarme quando o prazo estoura. */}
            <div>
              <label className='block text-xs font-medium text-slate-300 mb-1'>Quem prometeu, onde e quando</label>
              <input
                type='text'
                value={form.commitmentSource}
                onChange={(e) => setForm({ ...form, commitmentSource: e.target.value.slice(0, 300) })}
                placeholder='Ana Zanoni, e-mail de 21/09/2026 09h26'
                className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
              />
              <p className='text-[10px] text-slate-500 mt-1'>É o que se cita na cobrança seguinte. Vale e-mail, WhatsApp, telefone ou despacho no processo.</p>
            </div>
            <div>
              <label className='block text-xs font-medium text-slate-300 mb-1'>Nota</label>
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value.slice(0, 500) })} rows={3} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
            </div>
        </CascaDeModal>
      )}

      {/* ── Linha do tempo ───────────────────────────────────────────────────
          A `note` da anualidade responde "como está". Isto responde "por que
          está assim", que é a pergunta que ninguém consegue responder depois de
          três meses. E-mail e WhatsApp são canais de primeira classe aqui
          porque é por onde a coisa de fato anda. */}
      {/* DOSSIÊ DO CONTRATO — a regra, a escada e a conversa, num lugar só. */}
      {dossie && (
        // Só leitura: fecha ao clicar fora, como já era. "Mensal hoje" vai para a faixa;
        // "Abrir assinatura" (o caminho para editar) vai para o rodapé, neutro.
        <CascaDeModal
          largura='lg'
          fecharAoClicarFora
          titulo={dossie.carregando || dossieLoading ? 'Montando o dossiê…' : (dossie.contrato?.cliente || 'Dossiê')}
          subtitulo={dossie.carregando || dossieLoading ? undefined : [
            `#${dossie.contrato?.id}`,
            dossie.contrato?.contractNumber ? `Contrato ${dossie.contrato.contractNumber}` : null,
            dossie.contrato?.contractStartDate ? `vigência ${fmtDate(dossie.contrato.contractStartDate)}${dossie.contrato?.contractEndDate ? ` a ${fmtDate(dossie.contrato.contractEndDate)}` : ''}` : null,
          ].filter(Boolean).join(' · ')}
          faixa={dossie.carregando || dossieLoading ? undefined : [{ k: 'Mensal hoje', v: fmtBRL(dossie.contrato?.mrr) }]}
          onClose={() => setDossie(null)}
          rodape={
            <>
              {/* O dossiê é leitura; quem edita é a assinatura. O caminho
                  para ela sai daqui, e não de cada lista da tela. */}
              {!(dossie.carregando || dossieLoading) && (
                <button
                  type='button'
                  onClick={() => {
                    const c = activeContracts.find((x: any) => Number(x.id) === Number(dossie.contrato?.id));
                    setDossie(null);
                    if (c && onEditContract) onEditContract(c);
                    else onOpenContracts?.();
                  }}
                  className='text-xs px-2.5 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
                  title='Abrir a assinatura para editar a regra, a data-base ou os anexos.'
                >
                  Abrir assinatura
                </button>
              )}
              <button type='button' onClick={() => setDossie(null)} className='ml-auto px-4 py-2 text-slate-300 hover:text-white text-sm'>Fechar</button>
            </>
          }
        >
            {dossie.carregando || dossieLoading ? (
              <div className='px-5 py-10 text-center text-slate-400 text-sm'>Montando o dossiê…</div>
            ) : (
              <>
                <div className='px-5 py-4 grid gap-4 lg:grid-cols-2'>
                  {/* A REGRA, com a fonte junto: sem ela todo número é afirmação sem lastro. */}
                  <div className='bg-central-elevado/50 border border-central-borda rounded-lg overflow-hidden'>
                    <p className='px-4 py-2.5 border-b border-central-borda text-[10px] uppercase tracking-wider text-slate-500 font-semibold'>A regra, lida no documento</p>
                    <div className='px-4 py-3 text-sm space-y-2'>
                      <div className='flex gap-3'><span className='text-slate-500 w-24 shrink-0'>Índice</span><span className='text-white'>{dossie.contrato?.regra?.indice || '—'}</span></div>
                      <div className='flex gap-3'>
                        <span className='text-slate-500 w-24 shrink-0'>Data-base</span>
                        <span className='text-white'>
                          {dossie.contrato?.regra?.baseKind ? baseKindMeta(dossie.contrato.regra.baseKind)?.short || dossie.contrato.regra.baseKind : '—'}
                          {dossie.contrato?.regra?.baseDate
                            ? <> · {fmtDate(dossie.contrato.regra.baseDate)}</>
                            : <span className={TONE_TEXT.warning}> · data ainda desconhecida</span>}
                        </span>
                      </div>
                      <div className='flex gap-3'><span className='text-slate-500 w-24 shrink-0'>Quem aplica</span><span className='text-white'>{dossie.contrato?.regra?.exOfficio === false ? 'mediante pedido' : dossie.contrato?.regra?.exOfficio ? 'de ofício' : '—'}</span></div>
                      <div className='flex gap-3'><span className='text-slate-500 w-24 shrink-0'>Fonte</span><span className='text-slate-300'>{dossie.contrato?.regra?.fonte || <span className='text-amber-400'>não anotada</span>}</span></div>
                      {dossie.contrato?.regra?.conferidaEm ? (
                        <p className='text-xs text-slate-400 pt-1'>Regra conferida em {fmtDate(dossie.contrato.regra.conferidaEm)}.</p>
                      ) : (
                        <p className='text-xs text-amber-400/90 pt-1'>
                          Não marcada como conferida
                          {dossie.contrato?.regra?.infoPedidaEm
                            ? <> — falta a data-base, pedida ao órgão em {fmtDate(dossie.contrato.regra.infoPedidaEm)}.</>
                            : dossie.contrato?.regra?.baseDate ? '.' : ' — falta a data-base e ainda não perguntamos.'}
                        </p>
                      )}
                      {dossie.contrato?.regra?.nota && (
                        <p className='text-xs text-slate-400 bg-central-fundo/60 border border-central-borda rounded p-2 mt-1 whitespace-pre-wrap'>{dossie.contrato.regra.nota}</p>
                      )}
                      {dossie.contrato?.anexos?.length > 0 && (
                        <div className='flex flex-wrap gap-1 pt-1'>
                          {dossie.contrato.anexos.map((an: any, i: number) => (
                            <span key={i} className='text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300' title={an.categoria || ''}>{an.arquivo}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* A ESCADA — é aqui que se vê degrau partindo da base errada. */}
                  <div className='bg-central-elevado/50 border border-central-borda rounded-lg overflow-hidden'>
                    <p className='px-4 py-2.5 border-b border-central-borda text-[10px] uppercase tracking-wider text-slate-500 font-semibold'>A escada de valores</p>
                    <div className='px-4 py-3'>
                      {!dossie.escada?.length && <p className='text-sm text-slate-500'>Nenhuma anualidade registrada ainda.</p>}
                      {dossie.escada?.map((a: any, i: number) => {
                        const anterior = dossie.escada[i - 1];
                        const quebra = anterior && Number(anterior.amountAfter) !== Number(a.amountBefore);
                        const m = adjustmentStatusMeta(a.status);
                        return (
                          <div key={a.id} className={`py-2.5 ${i > 0 ? 'border-t border-central-borda' : ''}`}>
                            <div className='flex items-center gap-2 flex-wrap'>
                              <span className='text-sm text-white font-medium'>{a.sequence}ª · {fmtDate(a.anniversaryDate)}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.badge}`}>{m.label}</span>
                              {a.isProvisional && <span className={`text-[10px] px-1.5 py-0.5 rounded ${TONE_BADGE.warning}`}>provisório</span>}
                            </div>
                            <p className='text-sm text-slate-300 mt-0.5'>
                              {fmtBRL(a.amountBefore)} <span className='text-slate-600'>→</span> <span className='text-white'>{fmtBRL(a.amountAfter)}</span>
                              <span className='text-slate-500 text-xs'> · {a.indexName} {fmtPct(a.percentage)}</span>
                            </p>
                            {quebra && (
                              <p className='text-xs text-red-400 mt-1'>
                                Este degrau parte de {fmtBRL(a.amountBefore)}, mas o anterior termina em {fmtBRL(anterior.amountAfter)}. Ou o de cima não foi aplicado, ou este precisa ser recomposto.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* O PRÓXIMO E-MAIL — o lembrete mora no sistema, e ao ser
                      marcado como enviado vira linha datada na conversa. */}
                  <div className='bg-central-elevado/50 border border-central-borda rounded-lg overflow-hidden lg:col-span-2'>
                    <div className='px-4 py-2.5 border-b border-central-borda flex items-center gap-3'>
                      <p className='text-[10px] uppercase tracking-wider text-slate-500 font-semibold'>O próximo e-mail</p>
                      {dossie.rascunho?.atualizadoEm && (
                        <span className='text-[11px] text-slate-500'>guardado em {fmtDate(dossie.rascunho.atualizadoEm)}</span>
                      )}
                      <button
                        type='button'
                        onClick={() => setRascunhoAberto(!rascunhoAberto)}
                        className='ml-auto text-xs text-slate-400 hover:text-white'
                      >
                        {rascunhoAberto ? 'fechar' : dossie.rascunho ? 'editar' : 'escrever'}
                      </button>
                    </div>
                    <div className='px-4 py-3'>
                      {!rascunhoAberto && !dossie.rascunho && (
                        <p className='text-sm text-slate-500'>Nenhum rascunho guardado. Serve para o pedido que ainda não virou ofício, como perguntar a data-base que falta.</p>
                      )}
                      {!rascunhoAberto && dossie.rascunho && (
                        <div className='space-y-2'>
                          <p className='text-xs text-slate-500'>
                            para <span className='text-slate-300'>{dossie.rascunho.para || '—'}</span>
                            {dossie.rascunho.copia && <> · cópia <span className='text-slate-300'>{dossie.rascunho.copia}</span></>}
                          </p>
                          <p className='text-sm text-white'>{dossie.rascunho.assunto}</p>
                          <pre className='text-xs text-slate-300 bg-central-fundo/60 border border-central-borda rounded p-3 whitespace-pre-wrap font-sans'>{dossie.rascunho.corpo}</pre>
                          <div className='flex gap-2'>
                            <button
                              type='button'
                              onClick={() => {
                                navigator.clipboard?.writeText(String(dossie.rascunho.corpo || ''));
                                showToast('Corpo do e-mail copiado', 'success');
                              }}
                              className='text-xs px-2.5 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:border-slate-500'
                            >
                              Copiar corpo
                            </button>
                            <button
                              type='button'
                              disabled={busy === 'rascunho'}
                              onClick={marcarRascunhoEnviado}
                              className='text-xs px-2.5 py-1.5 rounded-lg bg-primary hover:bg-primary-strong text-white disabled:opacity-50'
                              title='Guarda na linha do tempo que este e-mail saiu hoje e limpa o rascunho.'
                            >
                              {busy === 'rascunho' ? 'registrando…' : 'Marcar como enviado'}
                            </button>
                          </div>
                        </div>
                      )}
                      {rascunhoAberto && (
                        <div className='space-y-2'>
                          <div className='grid sm:grid-cols-2 gap-2'>
                            <input type='text' value={rascunho.para || ''} onChange={(e) => setRascunho({ ...rascunho, para: e.target.value })} placeholder='para' className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                            <input type='text' value={rascunho.copia || ''} onChange={(e) => setRascunho({ ...rascunho, copia: e.target.value })} placeholder='cópia' className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                          </div>
                          <input type='text' value={rascunho.assunto || ''} onChange={(e) => setRascunho({ ...rascunho, assunto: e.target.value })} placeholder='assunto' className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                          <textarea rows={8} value={rascunho.corpo || ''} onChange={(e) => setRascunho({ ...rascunho, corpo: e.target.value })} placeholder='corpo do e-mail' className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                          <div className='flex gap-2'>
                            <button type='button' disabled={busy === 'rascunho'} onClick={salvarRascunho} className='text-xs px-2.5 py-1.5 rounded-lg bg-primary hover:bg-primary-strong text-white disabled:opacity-50'>
                              {busy === 'rascunho' ? 'guardando…' : 'Guardar rascunho'}
                            </button>
                            <button type='button' onClick={() => { setRascunho({}); }} className='text-xs px-2.5 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white' title='Limpar os campos. Guardar em seguida apaga o rascunho.'>
                              Limpar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* A CONVERSA — unificada, porque o e-mail do órgão não respeita a fronteira do ciclo. */}
                  <div className='bg-central-elevado/50 border border-central-borda rounded-lg overflow-hidden lg:col-span-2'>
                    <p className='px-4 py-2.5 border-b border-central-borda text-[10px] uppercase tracking-wider text-slate-500 font-semibold'>
                      Linha do tempo do contrato
                      <span className='ml-2 normal-case tracking-normal text-slate-600'>todas as anualidades juntas</span>
                    </p>
                    <div className='px-4 py-3'>
                      {!dossie.linhaDoTempo?.length && <p className='text-sm text-slate-500'>Nada registrado ainda.</p>}
                      {dossie.linhaDoTempo?.map((e: any, i: number) => (
                        <div key={`${e.adjustmentId}-${e.id ?? 'd' + i}`} className={`flex gap-3 py-2 text-sm ${i > 0 ? 'border-t border-central-borda' : ''}`}>
                          <span className='text-xs text-slate-500 w-20 shrink-0 pt-0.5'>{fmtDate(e.occurredOn)}</span>
                          <span className='text-[10px] text-slate-500 w-16 shrink-0 pt-1'>{e.channel}</span>
                          <span className='min-w-0'>
                            <span className='block text-slate-200'>{e.summary}</span>
                            <span className='block text-[11px] text-slate-500 mt-0.5'>
                              {e.sequence ? <>{e.sequence}ª anualidade</> : <span className='text-slate-600'>contrato</span>}
                              {e.counterpart && <> · {e.counterpart}</>}
                              {e.derived && <> · registro automático</>}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
        </CascaDeModal>
      )}

      {timeline && (
        // Tem o registro de evento digitado em cima: antes fechava ao clicar fora;
        // agora só pelo × ou Fechar. O "Registrar" é a ação que grava, no corpo.
        <CascaDeModal
          largura='base'
          titulo='Linha do tempo'
          subtitulo={`${clientName(timeline.contract || {})} · ${timeline.sequence}ª anualidade de ${fmtDate(timeline.anniversaryDate)}`}
          onClose={() => setTimeline(null)}
          corpoClassName=''
          rodape={<button onClick={() => setTimeline(null)} className='ml-auto px-4 py-2 text-slate-300 hover:text-white text-sm'>Fechar</button>}
        >

            <div className='px-5 py-4 space-y-3'>
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-2'>
                <div>
                  <label className='block text-[10px] uppercase tracking-wide text-slate-500 mb-1'>Quando</label>
                  <input type='date' value={novoEvento.occurredOn || ''} onChange={(e) => setNovoEvento({ ...novoEvento, occurredOn: e.target.value })} className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                </div>
                <div>
                  <label className='block text-[10px] uppercase tracking-wide text-slate-500 mb-1'>Canal</label>
                  <select value={novoEvento.channel || 'email'} onChange={(e) => setNovoEvento({ ...novoEvento, channel: e.target.value })} className={`w-full px-2 py-1.5 ${campo} rounded text-xs`}>
                    {CANAIS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className='block text-[10px] uppercase tracking-wide text-slate-500 mb-1'>Sentido</label>
                  <select value={novoEvento.direction || 'recebido'} onChange={(e) => setNovoEvento({ ...novoEvento, direction: e.target.value })} className={`w-full px-2 py-1.5 ${campo} rounded text-xs`}>
                    <option value='recebido'>recebemos</option>
                    <option value='enviado'>enviamos</option>
                    <option value='interno'>interno</option>
                  </select>
                </div>
                <div>
                  <label className='block text-[10px] uppercase tracking-wide text-slate-500 mb-1'>Quem, do lado de lá</label>
                  <input type='text' value={novoEvento.counterpart || ''} onChange={(e) => setNovoEvento({ ...novoEvento, counterpart: e.target.value.slice(0, 200) })} placeholder='Ana Zanoni' className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                </div>
              </div>
              <div>
                <textarea
                  value={novoEvento.summary || ''}
                  onChange={(e) => setNovoEvento({ ...novoEvento, summary: e.target.value.slice(0, 500) })}
                  rows={2}
                  placeholder='O que aconteceu. Ex.: respondeu que o aditivo de prazo é assinado até 06/10 e o reajuste vem por apostilamento em seguida.'
                  className={`w-full px-3 py-2 ${campo} rounded text-sm`}
                />
                <div className='flex items-center justify-between mt-1'>
                  <p className='text-[10px] text-slate-500'>A hora, quando importa, cabe no texto: o campo de data guarda só o dia.</p>
                  <button
                    onClick={salvarEvento}
                    disabled={busy === 'evento' || eventsLoading || eventsErro !== null || String(novoEvento.summary || '').trim().length < 5}
                    title={eventsErro ? 'Registrar espera a linha do tempo ser lida: sem ela, não dá para saber se o evento já está lá' : undefined}
                    className='px-3 py-1.5 text-xs bg-primary hover:bg-primary-strong text-white rounded disabled:opacity-40'
                  >
                    {busy === 'evento' ? 'Registrando…' : 'Registrar'}
                  </button>
                </div>
              </div>
            </div>

            <div className='px-5 pb-5'>
              {eventsLoading && <p className='text-xs text-slate-500'>Carregando…</p>}
              {!eventsLoading && eventsErro && (
                <p role='alert' className={`text-xs ${TONE_TEXT.warning}`} data-linha-do-tempo-nao-lida>
                  Não consegui ler a linha do tempo ({eventsErro}).{' '}
                  <button type='button' onClick={() => abrirTimeline(timeline)} className='underline font-medium'>Tentar de novo</button>
                </p>
              )}
              {!eventsLoading && !eventsErro && events.length === 0 && (
                <p className='text-xs text-slate-500'>Nada registrado ainda.</p>
              )}
              <ul className='space-y-2'>
                {events.map((ev, i) => {
                  const canal = CANAIS.find((c) => c.value === ev.channel);
                  return (
                    <li key={ev.id ? `m${ev.id}` : `d${i}`} className='flex gap-3 items-start border-l-2 border-central-borda pl-3 py-1'>
                      <span className='text-[10px] font-mono text-slate-500 whitespace-nowrap pt-0.5 w-[74px]'>{fmtDate(ev.occurredOn)}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${ev.derived ? 'bg-slate-700 text-slate-400' : canal?.badge || 'bg-slate-700 text-slate-300'}`}>
                        {canal?.label || ev.channel}
                      </span>
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm text-slate-200 break-words'>{ev.summary}</p>
                        <p className='text-[10px] text-slate-500'>
                          {ev.direction === 'enviado' ? 'nós enviamos' : ev.direction === 'recebido' ? 'recebemos' : 'interno'}
                          {ev.counterpart ? ` · ${ev.counterpart}` : ''}
                          {ev.derived ? ' · do próprio sistema' : ''}
                        </p>
                      </div>
                      {!ev.derived && (
                        <button onClick={() => apagarEvento(ev.id)} className='p-1 text-slate-600 hover:text-red-400' title='Apagar registro errado'>
                          <Icon name='trash' className='w-3.5 h-3.5' />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
        </CascaDeModal>
      )}
    </div>
  );
};

export default ReajustesTab;
